import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getSupabase } from '../lib/supabase';
import { REINOS, PortalReino } from '../lib/portalData';
import { AuthCard } from '../components/portal/AuthCard';
import { PortalViews } from '../components/portal/PortalViews';
import { useReaderState } from '../lib/readerState';
type PortalState = 'auth' | 'library' | 'reino' | 'reader';
const reinos = REINOS;
export function Portal() {
  const [view, setView] = useState<PortalState>('auth');
  const [selectedReinoId, setSelectedReinoId] = useState<string | null>(null);
  const [selectedDocumentKey, setSelectedDocumentKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Access state — populated by loadUserAccess()
  const [userAccess, setUserAccess] = useState<any | null>(null);
  const [availableRealms, setAvailableRealms] = useState<PortalReino[]>([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState('');
  const supabase = getSupabase();
  const {
    setReader
  } = useReaderState();
  // Publish reader state to the global navbar so it can show the
  // "Volver a Tus documentos privados" button while a PDF is open.
  useEffect(() => {
    if (view === 'reader') {
      setReader({
        isReading: true,
        onExit: () => {
          setSelectedDocumentKey(null);
          setView('reino');
        }
      });
    } else {
      setReader({
        isReading: false
      });
    }
    return () => {
      setReader({
        isReading: false
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  // ─── Carga de acceso del usuario ───────────────────────────────────────
  // Esta función debe ejecutarse:
  //  1. Cuando la página carga si ya existe sesión activa.
  //  2. Después del login exitoso (a través del listener onAuthStateChange).
  const loadUserAccess = async () => {
    if (!supabase) {
      setError('No pudimos cargar tu acceso. Intenta de nuevo.');
      return;
    }
    try {
      setError('');
      const {
        data: {
          session
        },
        error: sessionError
      } = await supabase.auth.getSession();
      const userEmail = session?.user?.email;
      console.log('SESSION:', session);
      console.log('SESSION ERROR:', sessionError);
      console.log('USER EMAIL:', userEmail);
      if (sessionError || !userEmail) {
        setError('No hay sesión activa.');
        return;
      }
      const {
        data: accessData,
        error: accessError
      } = await supabase.from('user_access').select('*').ilike('email', userEmail).maybeSingle();
      console.log('ACCESS DATA:', accessData);
      console.log('ACCESS ERROR:', accessError);
      if (accessError) {
        setError('No pudimos cargar tu acceso. Intenta de nuevo.');
        return;
      }
      if (!accessData) {
        setError('Tu cuenta no tiene acceso activo.');
        return;
      }
      setUserAccess(accessData);
      if (accessData.product_key === 'LOS12-COMPLETO' || accessData.access_type === 'full' || accessData.realm === 'todos') {
        setAvailableRealms(reinos);
        setHasAccess(true);
        return;
      }
      if (accessData.access_type === 'single') {
        const allowedRealms = reinos.filter(reino => reino.id === accessData.realm);
        setAvailableRealms(allowedRealms);
        setHasAccess(true);
        return;
      }
      if (accessData.access_type === 'multi') {
        const allowedRealms = (accessData.realm || '').split(',').map((item: string) => item.trim().toLowerCase()).filter((item: string) => item.length > 0);
        const filteredRealms = reinos.filter(reino => allowedRealms.includes(reino.id.toLowerCase()));
        setAvailableRealms(filteredRealms);
        setHasAccess(true);
        return;
      }
      setError('Tu cuenta no tiene acceso activo.');
    } catch (err) {
      console.error('LOAD USER ACCESS ERROR:', err);
      setError('No pudimos cargar tu acceso. Intenta de nuevo.');
    }
  };
  // Reset everything when user logs out
  const resetAccessState = () => {
    setUserAccess(null);
    setAvailableRealms([]);
    setHasAccess(false);
    setError('');
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Initial session check — DO NOT decide on a view until this resolves.
    // While getSession() is pending, `loading` stays true and the spinner
    // is shown, which prevents the "flash of auth screen" that causes the
    // refresh redirect loop on the portal route.
    const checkInitialSession = async () => {
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session) {
          await loadUserAccess();
          if (cancelled) return;
          setView('library');
        } else {
          // Only now that we know for certain there is no session,
          // we show the auth view.
          setView('auth');
        }
      } catch (err) {
        console.error('GET SESSION ERROR:', err);
        if (cancelled) return;
        setView('auth');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkInitialSession();
    // Auth state subscription — runs after login/logout.
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;
      if (session) {
        await loadUserAccess();
        if (cancelled) return;
        setView(prev => prev === 'auth' ? 'library' : prev);
      } else {
        resetAccessState();
        setView('auth');
        setSelectedReinoId(null);
        setSelectedDocumentKey(null);
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);
  const handleLogout = () => {
    // Reset UI state synchronously so the user lands on the login screen
    // immediately — no waiting on Supabase's network round-trip.
    resetAccessState();
    setSelectedReinoId(null);
    setSelectedDocumentKey(null);
    setView('auth');
    // Fire-and-forget the actual sign-out. The onAuthStateChange listener
    // will re-confirm the logged-out state when it resolves.
    if (supabase) {
      supabase.auth.signOut().catch(err => {
        console.error('SIGN OUT ERROR:', err);
      });
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-cosmic-black flex items-center justify-center" data-id="element-1170">
        <div className="w-12 h-12 border-2 border-gold-primary/20 border-t-gold-primary rounded-full animate-spin" data-id="element-1171" />
      </div>;
  }
  return <div className="min-h-screen bg-cosmic-black relative overflow-hidden pt-24 pb-20 px-4 md:px-6" data-id="element-1172">
      <div className="absolute inset-0 pointer-events-none" data-id="element-1173">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.05) 0%, transparent 70%)'
      }} data-id="element-1174" />
      </div>

      <div className="relative z-10 container mx-auto" data-id="element-1175">
        {view !== 'auth' && <div className="flex justify-between items-center mb-12 max-w-6xl mx-auto" data-id="element-1176">
            <h1 className="text-xl font-serif font-bold text-white tracking-widest uppercase" data-id="element-1177">
              Portal de Usuario
            </h1>
            <button onClick={handleLogout} className="text-xs font-serif tracking-[0.15em] text-white/50 hover:text-white transition-colors uppercase" data-id="element-1178">
              Cerrar Sesión
            </button>
          </div>}

        {view === 'auth' && <div className="text-center mb-12" data-id="element-1179">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" data-id="element-1180">
              Portal de Usuario
            </h1>
            <p className="text-gold-light/80 font-serif italic text-lg max-w-xl mx-auto" data-id="element-1181">
              Accede a tu Reino interior y continúa tu experiencia privada de
              lectura.
            </p>
          </div>}

        <motion.div key={view} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} data-id="element-1182">
          {view === 'auth' ? <AuthCard onAuthenticated={() => setView('library')} data-id="element-1183" /> : !hasAccess ? <div className="max-w-md mx-auto text-center bg-cosmic-black/80 border border-red-500/20 rounded-2xl p-8" data-id="element-1184">
              <p className="text-red-400 font-serif italic text-lg mb-6" data-id="element-1185">
                {error || 'Tu cuenta no tiene acceso activo.'}
              </p>
              <button onClick={handleLogout} className="px-6 py-2 rounded-lg border border-gold-primary/30 text-gold-light text-sm tracking-widest uppercase hover:bg-gold-primary/10 transition-colors" data-id="element-1186">
                Cerrar sesión
              </button>
            </div> : <PortalViews view={view} userAccess={userAccess} availableRealms={availableRealms} selectedReinoId={selectedReinoId} selectedDocumentKey={selectedDocumentKey} onSelectReino={id => {
          setSelectedReinoId(id);
          setView('reino');
        }} onSelectDocument={key => {
          setSelectedDocumentKey(key);
          setView('reader');
        }} onBackToLibrary={() => {
          setSelectedReinoId(null);
          setView('library');
        }} onBackToReino={() => {
          setSelectedDocumentKey(null);
          setView('reino');
        }} data-id="element-1187" />}
        </motion.div>
      </div>
    </div>;
}